import { openModal, el } from '@bn-games/ui-shell';
import {
  DRAND_DISPLAY_NAME,
  DRAND_QUICKNET_CHAIN_HASH,
  DRAND_SIGNER_LABEL,
  verifySeedProof,
  type DiceRollProof,
  type ProofStatus,
} from '@bn-games/verifiable-rng';
import { t } from '../i18n/I18n.js';
import type { GameRecord } from '../history/types.js';

interface HandEntry {
  readonly handIndex: number;
  readonly seed: number;
  readonly proof: DiceRollProof | null;
}

interface RowState {
  readonly entry: HandEntry;
  status: ProofStatus | 'pending';
  rowEl: HTMLElement;
  statusEl: HTMLElement;
}

function collectHands(record: GameRecord): HandEntry[] {
  // Hand 0 is the initial deal, encoded on RecordedSeed itself; subsequent
  // hands come in as `next-hand` events with their own seed + optional proof.
  const out: HandEntry[] = [
    {
      handIndex: 0,
      seed: record.seed.seed,
      proof: record.seed.proof ?? null,
    },
  ];
  let n = 1;
  for (const move of record.moves) {
    if (move.kind === 'next-hand') {
      out.push({ handIndex: n, seed: move.seed, proof: move.proof ?? null });
      n += 1;
    }
  }
  return out;
}

function dateLabel(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortHex(hex: string, head = 6, tail = 4): string {
  if (hex.length <= head + tail + 1) return hex;
  return `${hex.slice(0, head)}…${hex.slice(-tail)}`;
}

function statusGlyph(status: ProofStatus | 'pending'): string {
  switch (status) {
    case 'verified':
      return '✓';
    case 'value-mismatch':
    case 'sig-invalid':
      return '✕';
    case 'unverified':
      return '⚠';
    case 'pending':
      return '…';
  }
}

function statusLabel(status: ProofStatus | 'pending'): string {
  switch (status) {
    case 'verified':
      return t('verify.statusVerified');
    case 'value-mismatch':
      return t('verify.statusSeedMismatch');
    case 'sig-invalid':
      return t('verify.statusSigInvalid');
    case 'unverified':
      return t('verify.statusUnverified');
    case 'pending':
      return t('verify.statusPending');
  }
}

function statusClass(status: ProofStatus | 'pending'): string {
  switch (status) {
    case 'verified':
      return 'verify-row__status--ok';
    case 'value-mismatch':
    case 'sig-invalid':
      return 'verify-row__status--bad';
    case 'unverified':
      return 'verify-row__status--warn';
    case 'pending':
      return 'verify-row__status--pending';
  }
}

function applyStatus(row: RowState, next: ProofStatus | 'pending'): void {
  row.status = next;
  row.rowEl.classList.remove(
    'verify-row--verified',
    'verify-row--bad',
    'verify-row--warn',
    'verify-row--pending',
  );
  switch (next) {
    case 'verified':
      row.rowEl.classList.add('verify-row--verified');
      break;
    case 'value-mismatch':
    case 'sig-invalid':
      row.rowEl.classList.add('verify-row--bad');
      break;
    case 'unverified':
      row.rowEl.classList.add('verify-row--warn');
      break;
    case 'pending':
      row.rowEl.classList.add('verify-row--pending');
      break;
  }
  while (row.statusEl.firstChild) row.statusEl.removeChild(row.statusEl.firstChild);
  row.statusEl.classList.value = `verify-row__status ${statusClass(next)}`;
  row.statusEl.appendChild(el('span', { class: 'verify-row__glyph', text: statusGlyph(next) }));
  row.statusEl.appendChild(
    el('span', { class: 'verify-row__status-label', text: statusLabel(next) }),
  );
}

function buildRow(entry: HandEntry): { rowEl: HTMLElement; statusEl: HTMLElement } {
  const roundText =
    entry.proof !== null ? t('verify.roundTemplate', { n: entry.proof.round }) : '—';
  const statusEl = el('div', { class: 'verify-row__status' });
  const rowEl = el('div', { class: 'verify-row verify-row--pending' }, [
    el('div', { class: 'verify-row__label', text: t('verify.handLabel', { n: entry.handIndex + 1 }) }),
    el('div', {
      class: 'verify-row__detail',
      text: t('verify.seedTemplate', { seed: entry.seed }),
    }),
    el('div', { class: 'verify-row__round', text: roundText }),
    statusEl,
  ]);
  return { rowEl, statusEl };
}

function buildHeader(record: GameRecord): HTMLElement {
  const chainHash = record.seed.drandChainHash ?? DRAND_QUICKNET_CHAIN_HASH;
  return el('div', { class: 'verify-header' }, [
    el('div', { class: 'verify-header__row' }, [
      el('span', { class: 'verify-header__key', text: `${t('verify.source')}:` }),
      el('span', { class: 'verify-header__val', text: DRAND_DISPLAY_NAME }),
    ]),
    el('div', { class: 'verify-header__row' }, [
      el('span', { class: 'verify-header__key', text: `${t('verify.chain')}:` }),
      el('span', {
        class: 'verify-header__val verify-header__val--mono',
        text: shortHex(chainHash, 8, 6),
        attrs: { title: chainHash },
      }),
    ]),
    el('div', { class: 'verify-header__row' }, [
      el('span', { class: 'verify-header__key', text: `${t('verify.signedBy')}:` }),
      el('span', { class: 'verify-header__val', text: DRAND_SIGNER_LABEL }),
    ]),
    el('p', { class: 'verify-header__explainer', text: t('verify.explainer') }),
  ]);
}

interface ProofBundleEntry {
  readonly handIndex: number;
  readonly seed: number;
  readonly proof: DiceRollProof | null;
}

function buildProofBundle(record: GameRecord, hands: readonly HandEntry[]) {
  const entries: ProofBundleEntry[] = hands.map((h) => ({
    handIndex: h.handIndex,
    seed: h.seed,
    proof: h.proof,
  }));
  return {
    chainHash: record.seed.drandChainHash ?? DRAND_QUICKNET_CHAIN_HASH,
    matchId: record.id,
    finishedAt: new Date(record.finishedAt).toISOString(),
    entries,
  };
}

function downloadProofBundle(record: GameRecord, hands: readonly HandEntry[]): void {
  const blob = new Blob([JSON.stringify(buildProofBundle(record, hands), null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const ts = new Date(record.finishedAt).toISOString().replace(/[:.]/g, '-');
  const shortId = record.id.slice(0, 8);
  const a = document.createElement('a');
  a.href = url;
  a.download = `bn-domino-proof-${shortId}-${ts}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  queueMicrotask(() => URL.revokeObjectURL(url));
}

function updateSummary(summary: HTMLElement, rows: readonly RowState[]): void {
  const count = rows.length;
  let ok = 0;
  let bad = 0;
  let unverified = 0;
  let pending = 0;
  for (const r of rows) {
    switch (r.status) {
      case 'verified':
        ok += 1;
        break;
      case 'value-mismatch':
      case 'sig-invalid':
        bad += 1;
        break;
      case 'unverified':
        unverified += 1;
        break;
      case 'pending':
        pending += 1;
        break;
    }
  }
  let text: string;
  let cls: string;
  if (pending > 0) {
    text = t('verify.verifying');
    cls = 'verify-summary--pending';
  } else if (bad > 0) {
    text = t('verify.someFail', { ok, count, bad });
    cls = 'verify-summary--bad';
  } else if (unverified > 0) {
    text = t('verify.someUnverified', { ok, count, unverified });
    cls = 'verify-summary--warn';
  } else {
    text = t('verify.allOk', { count });
    cls = 'verify-summary--ok';
  }
  summary.className = `verify-summary ${cls}`;
  summary.textContent = text;
}

export function openVerifierModal(record: GameRecord): void {
  const hands = collectHands(record);
  const rows: RowState[] = hands.map((entry) => {
    const { rowEl, statusEl } = buildRow(entry);
    return { entry, status: 'pending', rowEl, statusEl };
  });

  const list = el(
    'div',
    { class: 'verify-list', attrs: { role: 'list' } },
    rows.map((r) => r.rowEl),
  );
  const summary = el('div', { class: 'verify-summary verify-summary--pending' });

  const body = el('div', { class: 'verify-modal-body' }, [
    buildHeader(record),
    list,
    summary,
  ]);

  const handle = openModal({
    title: t('verify.title', { date: dateLabel(record.finishedAt) }),
    body,
    actions: [
      { label: t('verify.downloadProof'), onClick: () => downloadProofBundle(record, hands) },
      { label: t('verify.close'), primary: true, onClick: () => handle.close() },
    ],
  });

  for (const row of rows) applyStatus(row, 'pending');
  updateSummary(summary, rows);

  // Records without a `roomCode` predate the verifiable rollout (or were
  // corrupted post-write). Skip BLS work and flag — running `verifySeedProof`
  // with `roomCode=''` would derive against the wrong tag and mis-label every
  // hand as `value-mismatch`.
  const roomCode = record.seed.roomCode ?? '';
  const roomCodeMissing = roomCode.length === 0;

  void (async () => {
    for (const row of rows) {
      if (row.entry.proof === null) {
        applyStatus(row, 'unverified');
        updateSummary(summary, rows);
        continue;
      }
      if (roomCodeMissing) {
        applyStatus(row, 'sig-invalid');
        updateSummary(summary, rows);
        continue;
      }
      const result = await verifySeedProof(row.entry.proof, roomCode, row.entry.seed);
      applyStatus(row, result.status);
      updateSummary(summary, rows);
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  })();
}
